import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useState } from 'react';
// DO NOT import @stream-io/video-react-sdk at the top level — it pulls in Node.js
// 'crypto' which breaks webpack on every page. Load it lazily inside initVideoClient().
import { GIFTS, MAX_ACTIVE_REACTIONS, MAX_CHAT_MESSAGES, STREAM_STATUS } from './constants';
import * as livestreamApi from '../../services/livestreamApi';
import { useSelector } from 'react-redux';
import i18n from '../../i18n';

const LiveStreamContext = createContext(null);

const initialState = {
  status: STREAM_STATUS.SETUP,
  streamTitle: '',
  streamDescription: '',
  isLive: false,
  startedAt: null,
  duration: 0,

  // GetStream identifiers
  liveStreamId: null,
  getstreamCallId: null,
  rtmpUrl: null,
  streamKey: null,
  hlsUrl: null,
  recordingEnabled: false,
  streamMode: 'webcam',
  creatorId: null,
  streamStats: null,

  selectedCamera: '',
  selectedMic: '',
  isCameraOn: true,
  isMicOn: true,

  viewerCount: 0,

  chatMessages: [],
  chatEnabled: true,

  recentTips: [],
  topGifters: [],
  activeTipBanner: null,
  activeGiftAnimation: null,
  activeReactions: [],
  activeTips: [],

  isChatVisible: true,
  isCreator: false,

  // History / pagination
  historyHasMore: false,
  historyOldestId: null,

  // Errors
  error: null,
  isConnecting: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STREAM_META':
      return { ...state, ...action.payload };

    case 'GO_LIVE':
      return {
        ...state,
        status: STREAM_STATUS.LIVE,
        isLive: true,
        startedAt: new Date(),
        duration: 0,
        isConnecting: false,
        error: null,
      };

    case 'END_STREAM':
      return {
        ...state,
        status: STREAM_STATUS.ENDED,
        isLive: false,
        streamStats: action.payload || null,
      };

    case 'TICK_DURATION':
      return { ...state, duration: state.duration + 1 };

    case 'ADD_CHAT_MESSAGE': {
      const msgs = [...state.chatMessages, action.payload];
      return { ...state, chatMessages: msgs.slice(-MAX_CHAT_MESSAGES) };
    }

    case 'LOAD_HISTORY': {
      const { messages, topGifters: historyGifters, hasMore } = action.payload;
      // Prepend historical messages before current ones
      const merged = [...messages, ...state.chatMessages];
      return {
        ...state,
        chatMessages: merged.slice(-MAX_CHAT_MESSAGES),
        topGifters: historyGifters.length > 0 ? historyGifters : state.topGifters,
        historyHasMore: hasMore,
        historyOldestId: messages.length > 0 ? messages[0].id : state.historyOldestId,
      };
    }

    case 'ADD_TIP': {
      const tip = action.payload;
      const newTips = [tip, ...state.recentTips].slice(0, 50);
      // Merge existing DB-loaded totals with session tips
      const gifterMap = {};
      // Start from existing top gifters (DB totals)
      for (const g of state.topGifters) {
        const uid = g.user?.id || g.user_id;
        if (uid) gifterMap[uid] = { user: g.user, totalAmount: g.totalAmount || 0 };
      }
      // Add the new tip amount to the tipper
      const tipUid = tip.user.id;
      if (!gifterMap[tipUid]) gifterMap[tipUid] = { user: tip.user, totalAmount: 0 };
      gifterMap[tipUid].totalAmount += tip.amount;
      const topGifters = Object.values(gifterMap).sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 10);
      const chatMsg = {
        id: `tipchat_${tip.id}`,
        user: tip.user,
        text: `${i18n.t('livestream:chat.tippedAmount', { amount: tip.amount })}${tip.message ? ` — "${tip.message}"` : ''}`,
        timestamp: tip.timestamp,
        type: 'tip',
      };
      return {
        ...state,
        recentTips: newTips,
        topGifters,
        activeTipBanner: tip,
        chatMessages: [...state.chatMessages, chatMsg].slice(-MAX_CHAT_MESSAGES),
      };
    }

    case 'CLEAR_TIP_BANNER':
      return { ...state, activeTipBanner: null };

    case 'SET_GIFT_ANIMATION':
      return { ...state, activeGiftAnimation: action.payload };

    case 'CLEAR_GIFT_ANIMATION':
      return { ...state, activeGiftAnimation: null };

    case 'ADD_REACTION': {
      const reactions = [...state.activeReactions, action.payload];
      return { ...state, activeReactions: reactions.slice(-MAX_ACTIVE_REACTIONS) };
    }

    case 'REMOVE_REACTION':
      return { ...state, activeReactions: state.activeReactions.filter((r) => r.id !== action.payload) };

    case 'ADD_TIP_BUBBLE': {
      const tips = [...state.activeTips, action.payload];
      return { ...state, activeTips: tips.slice(-MAX_ACTIVE_REACTIONS) };
    }

    case 'REMOVE_TIP_BUBBLE':
      return { ...state, activeTips: state.activeTips.filter((t) => t.id !== action.payload) };

    case 'UPDATE_VIEWER_COUNT':
      return { ...state, viewerCount: Math.max(0, action.payload) };

    case 'TOGGLE_CAMERA':
      return { ...state, isCameraOn: !state.isCameraOn };

    case 'TOGGLE_MIC':
      return { ...state, isMicOn: !state.isMicOn };

    case 'TOGGLE_CHAT':
      return { ...state, isChatVisible: !state.isChatVisible };

    case 'SET_DEVICES':
      return { ...state, ...action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isConnecting: false };

    case 'SET_CONNECTING':
      return { ...state, isConnecting: action.payload, error: null };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

let reactionIdCounter = 0;

export const LiveStreamProvider = ({ children, isCreator = false }) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, isCreator });
  const currentUser = useSelector((s) => s.auth?.user || s.user?.currentUser || null);

  const tipBannerTimer   = useRef(null);
  const giftAnimTimer    = useRef(null);
  const durationTimer    = useRef(null);
  const liveStreamIdRef  = useRef(null);

  // GetStream SDK refs (not stored in reducer — they're class instances)
  const videoClientRef = useRef(null);
  const callRef        = useRef(null);

  // ── Duration ticker (only while live) ─────────────────────────────────────
  useEffect(() => {
    if (state.isLive) {
      durationTimer.current = setInterval(() => dispatch({ type: 'TICK_DURATION' }), 1000);
    } else {
      if (durationTimer.current) clearInterval(durationTimer.current);
    }
    return () => { if (durationTimer.current) clearInterval(durationTimer.current); };
  }, [state.isLive]);

  // ── Auto-clear tip banner ──────────────────────────────────────────────────
  useEffect(() => {
    if (state.activeTipBanner) {
      if (tipBannerTimer.current) clearTimeout(tipBannerTimer.current);
      tipBannerTimer.current = setTimeout(() => dispatch({ type: 'CLEAR_TIP_BANNER' }), 4000);
    }
    return () => { if (tipBannerTimer.current) clearTimeout(tipBannerTimer.current); };
  }, [state.activeTipBanner]);

  // ── Auto-clear gift animation ──────────────────────────────────────────────
  useEffect(() => {
    if (state.activeGiftAnimation) {
      if (giftAnimTimer.current) clearTimeout(giftAnimTimer.current);
      const anim = state.activeGiftAnimation.gift?.animation || state.activeGiftAnimation.gift?.animation_type || 'float';
      const dur = anim === 'mega' ? 5000 : anim === 'confetti' ? 4000 : anim === 'burst' ? 3000 : 2500;
      giftAnimTimer.current = setTimeout(() => dispatch({ type: 'CLEAR_GIFT_ANIMATION' }), dur);
    }
    return () => { if (giftAnimTimer.current) clearTimeout(giftAnimTimer.current); };
  }, [state.activeGiftAnimation]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (callRef.current) {
        callRef.current.leave().catch(() => {});
        callRef.current = null;
      }
      if (videoClientRef.current) {
        videoClientRef.current.disconnectUser().catch(() => {});
        videoClientRef.current = null;
      }
    };
  }, []);

  // ── Subscribe to GetStream call events ────────────────────────────────────
  const subscribeToCallEvents = useCallback((call, userId, streamId) => {
    // Store streamId in ref so it's always current
    if (streamId) liveStreamIdRef.current = streamId;

    // Participant count changes → update viewer count
    call.on('call.session_participant_count_updated', (event) => {
      // GetStream SDK may use different property names across versions
      let count = 0;
      if (event.participants_count_by_role) {
        count = Object.values(event.participants_count_by_role).reduce((sum, n) => sum + n, 0);
      } else if (event.participantCount?.total != null) {
        count = event.participantCount.total;
      } else if (event.total != null) {
        count = event.total;
      }
      dispatch({ type: 'UPDATE_VIEWER_COUNT', payload: count });
      // Persist to backend using ref (never stale)
      const id = liveStreamIdRef.current;
      if (id) {
        livestreamApi.updateViewerCount(id, count).catch(() => {});
      }
    });

    // Also listen to call.session_participant_joined/left for more reliable counting
    call.on('call.session_participant_joined', () => {
      const session = call.state?.session;
      if (session) {
        const count = Object.values(session.participants_count_by_role || {}).reduce((sum, n) => sum + n, 0);
        if (count > 0) dispatch({ type: 'UPDATE_VIEWER_COUNT', payload: count });
      }
    });
    call.on('call.session_participant_left', () => {
      const session = call.state?.session;
      if (session) {
        const count = Object.values(session.participants_count_by_role || {}).reduce((sum, n) => sum + n, 0);
        dispatch({ type: 'UPDATE_VIEWER_COUNT', payload: count });
      }
    });

    // Custom events from other participants (tips, gifts, reactions)
    call.on('custom', (event) => {
      // GetStream wraps custom event payload inside event.custom
      const custom = event.custom || event;
      const customType = custom.type;
      const data = custom.data || custom;

      // Skip events sent by ourselves (we already added them optimistically)
      const senderId = data?.user?.id || event?.user?.id;
      if (senderId && String(senderId) === String(userId)) return;

      if (customType === 'livestream.tip') {
        dispatch({
          type: 'ADD_TIP',
          payload: {
            id: `tip_${Date.now()}`,
            user: data.user,
            amount: data.amount,
            message: data.message || '',
            timestamp: new Date(),
          },
        });
        reactionIdCounter += 1;
        dispatch({
          type: 'ADD_TIP_BUBBLE',
          payload: { id: `tipbubble_${reactionIdCounter}`, amount: data.amount, createdAt: Date.now() },
        });
      }

      if (customType === 'livestream.gift') {
        // Try hardcoded gifts first, then reconstruct from event data (custom gifts)
        const gift = GIFTS.find((g) => g.id === data.gift_id) || {
          id: data.gift_id,
          name: data.gift_name || 'Gift',
          emoji: data.gift_emoji || '🎁',
          image_url: data.gift_image_url || null,
          animation: data.gift_animation || 'float',
          animationDuration: data.gift_animation_duration || 3000,
          price: data.gift_price || 0,
        };

        dispatch({ type: 'SET_GIFT_ANIMATION', payload: { gift, user: data.user } });
        dispatch({
          type: 'ADD_CHAT_MESSAGE',
          payload: {
            id: `giftchat_${Date.now()}`,
            user: data.user,
            text: i18n.t('livestream:chat.sentGift', { emoji: gift.emoji || '🎁', name: gift.name }),
            timestamp: new Date(),
            type: 'gift',
          },
        });
      }

      if (customType === 'livestream.reaction') {
        reactionIdCounter += 1;
        dispatch({
          type: 'ADD_REACTION',
          payload: { id: `reaction_${reactionIdCounter}`, emoji: data.emoji, createdAt: Date.now() },
        });
      }

      if (customType === 'livestream.chat') {
        dispatch({
          type: 'ADD_CHAT_MESSAGE',
          payload: {
            id: `msg_${Date.now()}_${Math.random()}`,
            user: data.user,
            text: data.text,
            timestamp: new Date(data.timestamp),
            type: 'message',
            isOwn: data.user?.id === userId,
          },
        });
      }
    });
  }, []);

  // ── Initialize GetStream Video Client ─────────────────────────────────────
  // Uses a dynamic import so @stream-io/video-react-sdk is never bundled
  // into the main chunk — avoiding the Node.js 'crypto' error on other pages.
  const initVideoClient = useCallback(async (token, apiKey, userId, userName, avatarUrl) => {
    if (videoClientRef.current) {
      await videoClientRef.current.disconnectUser().catch(() => {});
    }

    const { StreamVideoClient } = await import('@stream-io/video-react-sdk');

    const client = new StreamVideoClient({
      apiKey,
      user: {
        id: String(userId),
        name: userName || String(userId),
        image: avatarUrl || undefined,
      },
      token,
    });

    videoClientRef.current = client;
    return client;
  }, []);

  // ── CREATOR: Start a new stream ───────────────────────────────────────────
  const startStream = useCallback(async ({ title, description, visibility, streamMode, thumbnail, price }) => {
    dispatch({ type: 'SET_CONNECTING', payload: true });
    try {
      const formData = new FormData();
      if (title)       formData.append('title', title);
      if (description) formData.append('description', description);
      formData.append('visibility', visibility || 'public');
      formData.append('stream_mode', streamMode || 'webcam');
      if (thumbnail)   formData.append('thumbnail', thumbnail);
      if (price && parseFloat(price) > 0) formData.append('price', price);

      const { data } = await livestreamApi.createStream(formData);
      if (!data.success) throw new Error(data.message || i18n.t('livestream:errors.createStreamFailed'));

      const {
        live_stream_id, getstream_call_id, getstream_token,
        getstream_api_key, rtmp_url, stream_key, hls_url, recording_enabled,
      } = data;

      // Init GetStream Video client
      await initVideoClient(
        getstream_token,
        getstream_api_key,
        currentUser?.id,
        currentUser?.display_name || currentUser?.username,
        currentUser?.avatar_url,
      );

      // Create/join the call (creator mode)
      const call = videoClientRef.current.call('livestream', getstream_call_id);
      callRef.current = call;

      if (streamMode !== 'rtmp') {
        // WebRTC mode — join and start publishing camera
        await call.join({ create: true });
        await call.camera.enable();
        await call.microphone.enable();
      }
      // RTMP mode: call is created on backend; no WebRTC join needed here

      // Store all stream meta in context
      dispatch({
        type: 'SET_STREAM_META',
        payload: {
          liveStreamId: live_stream_id,
          getstreamCallId: getstream_call_id,
          rtmpUrl: rtmp_url,
          streamKey: stream_key,
          hlsUrl: hls_url,
          recordingEnabled: recording_enabled,
          streamMode: streamMode || 'webcam',
          creatorId: currentUser?.id || null,
          streamTitle: title || '',
          streamDescription: description || '',
        },
      });

      subscribeToCallEvents(call, currentUser?.id, live_stream_id);

      // Read initial participant count
      try {
        const session = call.state?.session;
        if (session?.participants_count_by_role) {
          const count = Object.values(session.participants_count_by_role).reduce((s, n) => s + n, 0);
          if (count > 0) dispatch({ type: 'UPDATE_VIEWER_COUNT', payload: count });
        }
      } catch (_) {}

      // Tell the backend the stream is going live
      await livestreamApi.goLive(live_stream_id);
      dispatch({ type: 'GO_LIVE' });

      return { callId: getstream_call_id, liveStreamId: live_stream_id };
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err.message || i18n.t('livestream:errors.startStreamFailed') });
      throw err;
    }
  }, [currentUser, initVideoClient, subscribeToCallEvents]);

  // ── VIEWER: Join an existing stream ──────────────────────────────────────
  const joinAsViewer = useCallback(async (liveStreamId) => {
    dispatch({ type: 'SET_CONNECTING', payload: true });
    try {
      const { data } = await livestreamApi.getViewerToken(liveStreamId);
      if (!data.success) throw new Error(data.message || i18n.t('livestream:errors.accessDenied'));

      const { getstream_token, getstream_api_key, getstream_call_id, stream } = data;

      // Init GetStream Video client
      await initVideoClient(
        getstream_token,
        getstream_api_key,
        currentUser?.id || 0,
        currentUser?.display_name || currentUser?.username || 'Viewer',
        currentUser?.avatar_url,
      );

      const call = videoClientRef.current.call('livestream', getstream_call_id);
      callRef.current = call;
      await call.join();

      const streamCreatorId = stream?.creator?.id || null;
      const viewerIsCreator = currentUser?.id && streamCreatorId && String(currentUser.id) === String(streamCreatorId);

      dispatch({
        type: 'SET_STREAM_META',
        payload: {
          liveStreamId,
          getstreamCallId: getstream_call_id,
          streamTitle: stream?.title || '',
          creatorId: streamCreatorId,
          isCreator: viewerIsCreator || false,
          isConnecting: false,
        },
      });

      subscribeToCallEvents(call, currentUser?.id, liveStreamId);

      // Read initial participant count
      try {
        const session = call.state?.session;
        if (session?.participants_count_by_role) {
          const count = Object.values(session.participants_count_by_role).reduce((s, n) => s + n, 0);
          if (count > 0) dispatch({ type: 'UPDATE_VIEWER_COUNT', payload: count });
        }
      } catch (_) {}

      dispatch({ type: 'GO_LIVE' });

      // Load chat history + top gifters so late joiners can catch up
      try {
        const historyRes = await livestreamApi.getStreamHistory(liveStreamId);
        if (historyRes.data.success) {
          const { messages: rawMsgs, top_gifters: rawGifters, has_more } = historyRes.data;
          const messages = (rawMsgs || []).map(m => ({
            id: `hist_${m.id}`,
            dbId: m.id,
            user: m.user || { id: m.user_id, username: 'user', name: 'User' },
            text: m.message,
            timestamp: new Date(m.created_at),
            type: m.type || 'message',
            metadata: m.metadata,
          }));
          const topGifters = (rawGifters || []).map(g => ({
            user: g.user || { id: g.user_id },
            totalAmount: parseFloat(g.total_amount || 0),
          }));
          dispatch({ type: 'LOAD_HISTORY', payload: { messages, topGifters, hasMore: has_more } });
        }
      } catch (_) { /* history load is best-effort */ }

      return { call, callId: getstream_call_id };
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err.message || i18n.t('livestream:errors.joinStreamFailed') });
      throw err;
    }
  }, [currentUser, initVideoClient, subscribeToCallEvents]);

  // ── CREATOR: End the stream ────────────────────────────────────────────────
  const endStream = useCallback(async () => {
    let stats = null;
    try {
      if (callRef.current) {
        await callRef.current.endCall().catch(() => {});
        callRef.current = null;
      }
      const id = liveStreamIdRef.current || state.liveStreamId;
      if (id) {
        const { data } = await livestreamApi.endStream(id);
        stats = data.stats || null;
      }
    } catch (err) {
      console.error('End stream error:', err);
    }
    dispatch({ type: 'END_STREAM', payload: stats });
    return stats;
  }, [state.liveStreamId]);

  // ── Camera / Mic toggle ───────────────────────────────────────────────────
  const toggleCamera = useCallback(async () => {
    if (callRef.current) {
      const enabled = callRef.current.camera.state.status === 'enabled';
      if (enabled) await callRef.current.camera.disable().catch(() => {});
      else await callRef.current.camera.enable().catch(() => {});
    }
    dispatch({ type: 'TOGGLE_CAMERA' });
  }, []);

  const toggleMic = useCallback(async () => {
    if (callRef.current) {
      const enabled = callRef.current.microphone.state.status === 'enabled';
      if (enabled) await callRef.current.microphone.disable().catch(() => {});
      else await callRef.current.microphone.enable().catch(() => {});
    }
    dispatch({ type: 'TOGGLE_MIC' });
  }, []);

  // ── Chat ──────────────────────────────────────────────────────────────────
  const sendChatMessage = useCallback(async (text) => {
    if (!text.trim()) return;

    const userObj = {
      id: currentUser?.id || 0,
      username: currentUser?.username || 'you',
      name: currentUser?.display_name || currentUser?.username || 'You',
      avatar_url: currentUser?.avatar_url || null,
    };

    // Optimistic local add
    dispatch({
      type: 'ADD_CHAT_MESSAGE',
      payload: {
        id: `own_${Date.now()}`,
        user: userObj,
        text,
        timestamp: new Date(),
        type: 'message',
        isOwn: true,
      },
    });

    // Broadcast to all participants via custom event
    if (callRef.current) {
      try {
        await callRef.current.sendCustomEvent({
          type: 'livestream.chat',
          data: { user: userObj, text, timestamp: new Date().toISOString() },
        });
      } catch (err) {
        console.error('sendChatMessage error:', err);
      }
    }

    // Persist to DB (best-effort, don't block UI)
    const streamId = liveStreamIdRef.current || state.liveStreamId;
    if (streamId) {
      livestreamApi.sendChatMessage(streamId, text).catch(() => {});
    }
  }, [currentUser, state.liveStreamId]);

  // ── Tips ──────────────────────────────────────────────────────────────────
  const sendTip = useCallback(async (amount, message = '') => {
    const userObj = {
      id: currentUser?.id || 0,
      username: currentUser?.username || 'you',
      name: currentUser?.display_name || currentUser?.username || 'You',
      avatar_url: currentUser?.avatar_url || null,
    };

    // Broadcast via GetStream custom event so all viewers see it
    if (callRef.current) {
      await callRef.current.sendCustomEvent({
        type: 'livestream.tip',
        data: { user: userObj, amount, message },
      }).catch(() => {});
    }

    const tipId = `tip_${Date.now()}`;
    dispatch({
      type: 'ADD_TIP',
      payload: {
        id: tipId,
        user: userObj,
        amount,
        message,
        timestamp: new Date(),
      },
    });

    // Spawn floating $amount bubble
    reactionIdCounter += 1;
    dispatch({
      type: 'ADD_TIP_BUBBLE',
      payload: { id: `tipbubble_${reactionIdCounter}`, amount, createdAt: Date.now() },
    });
  }, [currentUser]);

  // ── Gifts ─────────────────────────────────────────────────────────────────
  const sendGift = useCallback(async (giftId, giftData = null) => {
    // Try hardcoded GIFTS first, then use passed giftData for custom/dynamic gifts
    const gift = GIFTS.find((g) => g.id === giftId) || giftData || { id: giftId, name: 'Gift', emoji: '🎁', animation: 'float' };

    const userObj = {
      id: currentUser?.id || 0,
      username: currentUser?.username || 'you',
      name: currentUser?.display_name || currentUser?.username || 'You',
      avatar_url: currentUser?.avatar_url || null,
    };

    // Broadcast via GetStream custom event — include full gift data for custom gifts
    if (callRef.current) {
      await callRef.current.sendCustomEvent({
        type: 'livestream.gift',
        data: {
          user: userObj,
          gift_id: giftId,
          gift_name: gift.name,
          gift_emoji: gift.emoji || '🎁',
          gift_image_url: gift.image_url || null,
          gift_animation: gift.animation || gift.animation_type || 'float',
          gift_animation_duration: gift.animationDuration || gift.animation_duration_ms || 3000,
          gift_price: gift.price || 0,
        },
      }).catch(() => {});
    }

    dispatch({ type: 'SET_GIFT_ANIMATION', payload: { gift, user: userObj } });
    dispatch({
      type: 'ADD_CHAT_MESSAGE',
      payload: {
        id: `giftchat_${Date.now()}`,
        user: userObj,
        text: `sent ${gift.emoji || '🎁'} ${gift.name}`,
        timestamp: new Date(),
        type: 'gift',
        isOwn: true,
      },
    });
  }, [currentUser]);

  // ── Reactions ─────────────────────────────────────────────────────────────
  const sendReaction = useCallback(async (emoji) => {
    reactionIdCounter += 1;
    const reactionId = `reaction_${reactionIdCounter}`;

    dispatch({ type: 'ADD_REACTION', payload: { id: reactionId, emoji, createdAt: Date.now() } });

    // Broadcast via GetStream
    if (callRef.current) {
      await callRef.current.sendCustomEvent({
        type: 'livestream.reaction',
        data: { emoji },
      }).catch(() => {});
    }
  }, []);

  const removeReaction = useCallback((id) => {
    dispatch({ type: 'REMOVE_REACTION', payload: id });
  }, []);

  const removeTip = useCallback((id) => {
    dispatch({ type: 'REMOVE_TIP_BUBBLE', payload: id });
  }, []);

  const toggleChat   = useCallback(() => dispatch({ type: 'TOGGLE_CHAT' }), []);
  const setStreamMeta = useCallback((meta) => dispatch({ type: 'SET_STREAM_META', payload: meta }), []);

  // Load older chat messages (scroll up)
  const loadMoreHistory = useCallback(async () => {
    if (!state.liveStreamId || !state.historyHasMore || !state.historyOldestId) return;
    try {
      const res = await livestreamApi.getStreamHistory(state.liveStreamId, state.historyOldestId);
      if (res.data.success) {
        const { messages: rawMsgs, has_more } = res.data;
        const messages = (rawMsgs || []).map(m => ({
          id: `hist_${m.id}`,
          dbId: m.id,
          user: m.user || { id: m.user_id, username: 'user', name: 'User' },
          text: m.message,
          timestamp: new Date(m.created_at),
          type: m.type || 'message',
          metadata: m.metadata,
        }));
        dispatch({ type: 'LOAD_HISTORY', payload: { messages, topGifters: [], hasMore: has_more } });
      }
    } catch (_) {}
  }, [state.liveStreamId, state.historyHasMore, state.historyOldestId]);

  // Expose the raw call object for StreamVideo component
  const getCall = useCallback(() => callRef.current, []);
  const getVideoClient = useCallback(() => videoClientRef.current, []);

  const value = {
    ...state,
    startStream,
    joinAsViewer,
    endStream,
    sendChatMessage,
    sendTip,
    sendGift,
    sendReaction,
    removeReaction,
    removeTip,
    toggleCamera,
    toggleMic,
    toggleChat,
    setStreamMeta,
    loadMoreHistory,
    getCall,
    getVideoClient,
    dispatch,
  };

  return (
    <LiveStreamContext.Provider value={value}>
      {children}
    </LiveStreamContext.Provider>
  );
};

export const useLiveStream = () => {
  const ctx = useContext(LiveStreamContext);
  if (!ctx) throw new Error('useLiveStream must be used within LiveStreamProvider');
  return ctx;
};

export default LiveStreamContext;
